<template>
  <div v-if="content" class="page-root-container">
    <div class="container-cms">
      <h1 class="container-cms__title">{{ content.title }}</h1>
      <HTMLContent :content="content.content" />
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, useFetch } from '@nuxtjs/composition-api';
import HTMLContent from '~/components/HTMLContent';
import { useGtm } from '~/composables';
import { useCache, CacheTagPrefix } from '@gemini/vsf-cache';
import { handleSeo } from '~/helpers/seo/handleSeo';

export default defineComponent({
  components: {
    HTMLContent,
  },
  props: {
    identifier: {
      type: [String],
      default: '',
    },
    content: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { pageGtmPush } = useGtm();
    const { addTags } = useCache();
    const { getCanonical } = handleSeo();
    const canonicalLink = computed(() => getCanonical());
    useFetch(async () => {
      pageGtmPush('CMS PAGE'); // @TODO fix this after implementing meta titles
      addTags([{ prefix: CacheTagPrefix.View, value: 'page' }]);
    });
    return {
      canonicalLink,
    };
  },
  head() {
    const meta = [];
    if (this.content.meta_description) {
      meta.push(
        {
          hid: 'description',
          name: 'description',
          content: this.content?.meta_description.replace(/(<([^>]+)>)/gi, ''),
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.content?.meta_description.replace(/(<([^>]+)>)/gi, ''),
        },
        // og:url
        {
          hid: 'og:url',
          name: 'og:url',
          content:
            'https://www.sognidicristallo.com' + this.$nuxt.$route.fullPath,
        },
        { hid: 'fb:app_id', property: 'fb:app_id', content: '208478719244524' }
      );
    }
    meta.push(
      { hid: 'robots', name: 'robots', content: 'index, follow' },
      {
        hid: 'og:title',
        name: 'og:title',
        content: this.content?.meta_title,
      }
    );

    return {
      title: this.content?.meta_title,
      meta,
      link: [{ ...this.canonicalLink }],
    };
  },
});
</script>

<style lang="scss" scoped>
.page-root-container {
  background-color: var(--_background-cms-primary);
  padding-top: 10px;
}
</style>

<style lang="scss">
.container-cms {
  margin: 50px auto 0;
  padding: 0 84px 135px 84px;
  max-width: 1366px;
  font-family: 'Spartan';
  font-size: 14px;
  line-height: 26px;
  @media screen and (max-width: 768px) {
    padding: 0 24px 50px 24px;
  }
  &__title {
    @include paragraph-l;
  }
  h4,
  h4 span,
  strong {
    font-size: var(--h4-font-size);
  }
  p {
    line-break: anywhere;
    strong {
      font-size: 14px;
    }
  }
  .table-wrap {
    overflow-x: scroll;
    table {
      border-collapse: collapse;
      margin: 25px 0;
      font-size: 0.9em;
      font-family: sans-serif;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
      width: 100%;
      tr:nth-of-type(1) {
        background-color: #43464e;
        color: #ffffff;
        text-align: left;
      }
      th,
      td {
        padding: 12px 15px;
        p {
          min-width: 100px;
          margin: 0;
        }
        &:nth-of-type(3) p {
          min-width: 300px;
        }
      }
      tbody tr {
        border-bottom: 1px solid #dddddd;
      }
      tbody tr:nth-of-type(even) {
        background-color: #f3f3f3;
      }
      tbody tr.active-row {
        font-weight: bold;
        color: #009879;
      }
      td:nth-of-type(1) {
        font-weight: bold;
      }
    }
  }
}
</style>
